<template>
  <el-table
    ref="table"
    v-loadmore="handelLoadmore"
    :data="currentData"
    :data-size="sortedRowData.length"
    :height-change-flag="heightChangeFlag"
    :span-method="onSpanMethod"
    :row-class-name="onRowClassName"
    :cell-class-name="onCellClassName"
    border
    class="dw-report"
    @sort-change="onSortChange"
    @cell-click="onCellClick"
  >
    <el-table-column type="index" :width="indexWidth">
      <template slot-scope="{row}">
        <div :class="mouseoverRowSeq===row._seq?'cell-focus':''">{{ row._seq }}</div>
      </template>
    </el-table-column>
    <el-table-column
      v-for="(item,index) in displayHeaders"
      :key="index"
      :label="getLetterSeq(index)"
      header-align="center"
      :label-class-name="mouseoverHeaderIndex===index?'header-focus':''"
    >
      <el-table-column
        :prop="item.name"
        :label="item.label"
        header-align="center"
        :align="getDefaultAlign(item)"
        sortable
        :sort-method="onSortMethod"
        :width="item._width"
        show-overflow-tooltip
      >
        <template slot-scope="{row}">
          <div @mouseenter="onMouseover(row._seq, index)" @mouseleave="onMouseover(-1, -1)">{{ toFormatter(item, row[item.name]) }}</div>
        </template>
      </el-table-column>
    </el-table-column>
  </el-table>
</template>

<script>
import { formatNumberToThousands, getLength } from '@/utils'
import ColumnUtils from '@/utils/ColumnUtils'
import { DocumentManage } from '@/utils/commonUtil'
import { isNumber2 } from '@/utils/validate'

// 26字母
const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']

export default {
  name: 'DwReport',
  props: {
    /*
    [...
      {
        dataType: 'string', // 数据类型
        display: false, // 是否显示
        group: false, // 是否分组
        name: 'name', // 字段名称
        label: 'label', // 标题名称
        precision: 0, // 精度
        subSum: false // 是否可小计
      }
    ...]
    */
    headers: { type: Array, required: true },
    rowData: { type: Array, required: true },
    cellClassName: { type: Function, default: null },
    subSumMaxNum: { type: Number, default: 2 }, // 小计的最大列数
    subSumDisiplayAll: { type: Boolean, default: true }, // 小计行显示所有数据
    showSum: { type: Boolean, default: true }, // 是否显示总计
    heightChangeFlag: { type: Boolean, default: false } // 组件高度发生改变标识
  },
  data() {
    return {
      sortHeader: null,
      sortType: '',
      sortedRowData: [],
      statisticsRows: [], // 统计行
      spanMatrixData: [], // 用于跨行计算的矩阵数据
      spanStartIndex: 1, // 开始进行跨行计算的索引
      startScrollLeft: 0, // 滚动起始的scrollLeft
      currentScrollTop: 0,
      currentStartIndex: 0,
      currentEndIndex: 0,
      showRowNum: 0,
      currentData: [],
      currentSpanData: [],
      mouseoverRowSeq: -1,
      mouseoverHeaderIndex: -1,
      documentManage: new DocumentManage(),
      timerId: 0 // 定时器
    }
  },
  computed: {
    displayHeaders() {
      return this.headers.filter(p => p.display)
    },
    indexWidth() {
      return String(20 + String(this.sortedRowData.length).length * 8)
    }
  },
  watch: {
    displayHeaders() {
      this.toGroup()
      this.toStatistics()
      this.onSortChange()
    },
    rowData() {
      this.toGroup()
      this.toStatistics()
      this.$nextTick(() => {
        this.onSortChange()
      })
    },
    subSumMaxNum() {
      this.toStatistics()
      this.onSortChange()
    },
    showSum() {
      this.toStatistics()
      this.onSortChange()
    },
    heightChangeFlag(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.$emit('update:heightChangeFlag', false)
        })
      }
    }
  },
  activated() {
    this.$refs.table.$refs.bodyWrapper.scrollTop = this.currentScrollTop
  },
  mounted() {
    this.documentManage.set('onScroll', e => {
      this.onScroll(e)
    })
    const bodyWrapper = this.$refs.table.$refs.bodyWrapper
    bodyWrapper.addEventListener('scroll', this.documentManage.get('onScroll'))
    this.toGroup()
    this.toStatistics()
    this.onSortChange()
  },
  beforeDestroy() {
    this.documentManage.destory()
  },
  methods: {
    handelLoadmore(currentStartIndex, currentEndIndex, showRowNum) {
      this.currentScrollTop = this.$refs.table.$refs.bodyWrapper.scrollTop
      this.currentStartIndex = currentStartIndex
      this.currentEndIndex = currentEndIndex
      this.showRowNum = showRowNum
      this.onPageChange()
    },
    getLetterSeq(num) {
      const remainder = num % 26
      const n = (num - remainder) / 26
      if (n > 0) {
        return this.getLetterSeq(n - 1) + LETTERS[remainder]
      }
      return LETTERS[remainder]
    },
    getDefaultAlign(header) {
      if (header.dataType === 'Double' || header.dataType === 'Integer') {
        return 'right'
      }
      return ''
    },
    toFormatter(header, value) {
      if (header.displayPrecision >= 0) {
        const num = Number(value)
        if (isNaN(num)) {
          return value
        } else {
          return formatNumberToThousands(ColumnUtils.formatNumber(value, header.displayPrecision))
        }
      }
      if (header.precision === 0) {
        return value
      }
      if (header.dataType === 'Double') {
        const num = Number(value)
        if (isNaN(num)) {
          return value
        } else {
          if (header.decimalPlaces) {
            // 后端已经处理好了小数位数，这里就不用管是否四舍五入，根据decimalPlaces，显示对应小数就行
            return formatNumberToThousands(ColumnUtils.numberOmitZeroColumnLabelFunc(value, Number(header.decimalPlaces)))
          }
          // 原仅保留两位
          return formatNumberToThousands(ColumnUtils.number2ColumnLabelFunc(value))
        }
      } else if (header.dataType === 'Integer') {
        const num = Number(value)
        if (isNaN(num)) {
          return value
        } else {
          return formatNumberToThousands(ColumnUtils.numberColumnLabelFunc(value))
        }
      }
      return value
    },
    // 进行分组编号
    toGroup() {
      if (this.rowData.length < 2) {
        return
      }
      for (const row of this.rowData) {
        delete row.groupNo
      }
      for (const header of this.displayHeaders) {
        let parentGroupNo = null
        let num = 1
        let preRow = this.rowData[0]
        if (preRow.groupNo) {
          parentGroupNo = preRow.groupNo
          preRow.groupNo.push(num)
        } else {
          preRow.groupNo = [num]
        }
        for (let i = 1; i < this.rowData.length; ++i) {
          const row = this.rowData[i]
          if (row.groupNo) {
            if (this.isEqualArray(row.groupNo, parentGroupNo) && row[header.name] === preRow[header.name]) {
              row.groupNo = preRow.groupNo
            } else {
              parentGroupNo = row.groupNo
              row.groupNo.push(++num)
            }
          } else {
            if (row[header.name] === preRow[header.name]) {
              row.groupNo = preRow.groupNo
            } else {
              row.groupNo = [++num]
            }
          }
          preRow = row
        }
      }
      if (this.rowData.length > 1) {
        /* 首行处理 */
        let row = this.rowData[0]
        let nextRow = this.rowData[1]
        let commonLength = this.getArrayCommonLength(row.groupNo, nextRow.groupNo)
        if (commonLength > 1) {
          row.groupNo = row.groupNo.slice(0, commonLength).join('-')
        } else {
          row.groupNo = String(row.groupNo[0])
        }
        /* 尾行处理 */
        let preRow = this.rowData[this.rowData.length - 2]
        row = this.rowData[this.rowData.length - 1]
        commonLength = this.getArrayCommonLength(preRow.groupNo, row.groupNo)
        if (commonLength > 1) {
          row.groupNo = row.groupNo.slice(0, commonLength).join('-')
        } else {
          row.groupNo = String(row.groupNo[0])
        }
        // 剩余行处理
        for (let i = 1; i < this.rowData.length - 1; ++i) {
          preRow = this.rowData[i - 1]
          row = this.rowData[i]
          nextRow = this.rowData[i + 1]
          const commonLength1 = this.getArrayCommonLength(preRow.groupNo, row.groupNo)
          const commonLength2 = this.getArrayCommonLength(row.groupNo, nextRow.groupNo)
          const maxLen = Math.max(commonLength1, commonLength2)
          if (maxLen > 1) {
            row.groupNo = row.groupNo.slice(0, maxLen).join('-')
          } else {
            row.groupNo = String(row.groupNo[0])
          }
        }
      }
    },
    isEqualArray(a, b) {
      if (a.length !== b.length) {
        return false
      }
      for (let i = 0; i < a.length; ++i) {
        if (a[i] !== b[i]) {
          return false
        }
      }
      return true
    },
    // 获取两个数据公共部分长度
    getArrayCommonLength(a, b) {
      const len = Math.min(a.length, b.length)
      for (let i = 0; i < len; ++i) {
        if (a[i] !== b[i]) {
          return i
        }
      }
      return 0
    },
    toStatistics() {
      this.statisticsRows = []
      /* 小计列数组 */
      const headers = []
      for (let i = 0; i < this.displayHeaders.length - 2 && i < this.subSumMaxNum; ++i) {
        if (!this.displayHeaders[i + 1].group) {
          break
        }
        headers.push(this.displayHeaders[i])
      }
      for (let i = headers.length - 1; i >= 0; --i) {
        if (!headers[i].subSum || !this.displayHeaders[i + 1].subSum) {
          headers.splice(i)
        }
      }
      if (headers.length > 0) {
        this.toSubSum(headers)
      }
      let sumColspan = 0
      if (headers.length > 0) {
        sumColspan = headers.length + 1
      } else {
        for (let i = 0; i < this.displayHeaders.length && i <= this.subSumMaxNum; ++i) {
          if (this.displayHeaders[i].dataType === 'Double' || this.displayHeaders[i].dataType === 'Integer') {
            break
          }
          sumColspan = i + 1
        }
      }
      // 总计
      this.toSum(sumColspan)
    },
    toSubSum(headers) {
      const displayHeaderTypes = [] // 每列的类型信息
      const map = new Map()
      for (let i = 0; i < this.rowData.length; ++i) {
        const row = this.rowData[i]
        if (i === 0) {
          for (const header of this.displayHeaders) {
            displayHeaderTypes.push(typeof row[header.name])
          }
        }
        let key
        for (const header of headers) {
          if (typeof key === 'undefined') {
            key = row[header.name]
          } else {
            key += '$' + row[header.name]
          }
          if (!header.subSum) {
            continue
          }
          const rows = map.get(key)
          if (rows) {
            rows.push(row)
          } else {
            map.set(key, [row])
          }
        }
      }
      const rows = []
      for (const [key, value] of map.entries()) {
        const keys = this.getKeysFromSplicingKey(key)
        for (let j = 0; j < keys.length; ++j) {
          if (displayHeaderTypes[j] === 'number') {
            const num = Number(keys[j])
            if (!isNaN(num)) {
              keys[j] = num
            }
          }
        }
        const row = { subSum: true, keys, subSumColspan: headers.length + 1 - keys.length }
        if (this.subSumDisiplayAll) {
          for (let j = 0; j < keys.length; ++j) {
            row[this.displayHeaders[j].name] = keys[j]
          }
        } else {
          row[this.displayHeaders[0].name] = keys[keys.length - 1]
        }
        row[this.displayHeaders[keys.length].name] = this.$t('CargoMsg.Cargo_SubTotal')
        for (let j = keys.length + 1; j < this.displayHeaders.length; ++j) {
          const header = this.displayHeaders[j]
          if (header.dataType === 'Double' || header.dataType === 'Integer') {
            row[header.name] = 0
            for (const item of value) {
              const num = Number(item[header.name])
              if (!isNaN(num)) {
                row[header.name] += num
              }
            }
          }
        }
        rows.push(row)
      }
      rows.sort(function(a, b) {
        return (a.keys.length - b.keys.length) * -1
      })
      this.statisticsRows.push(...rows)
    },
    // 从拼接的key中获取keys
    getKeysFromSplicingKey(key) {
      if (typeof key === 'string') {
        return key.split('$')
      }
      return [key]
    },
    toSum(sumColspan) {
      if (this.showSum && this.displayHeaders.length >= 2 && this.rowData.length > 0) {
        const obj = { sum: true, [this.displayHeaders[0].name]: this.$t('CargoMsg.Cargo_GrandTotal'), sumColspan }
        for (let i = 1; i < this.displayHeaders.length; ++i) {
          const header = this.displayHeaders[i]
          if (header.dataType === 'Double' || header.dataType === 'Integer') {
            obj[header.name] = 0
            for (const row of this.rowData) {
              const num = Number(row[header.name])
              if (!isNaN(num)) {
                obj[header.name] += num
              }
            }
          }
        }
        this.statisticsRows.push(obj)
      }
    },
    createSpanMatrixData() {
      const result = new Array(this.sortedRowData.length)
      for (let i = 0; i < result.length; ++i) {
        const row = this.sortedRowData[i]
        const arr = new Array(this.displayHeaders.length + 1) // 包含索引列
        arr[0] = { rowspan: 1, colspan: 1, fixed: true }
        if (row.subSum || row.sum) {
          let spaceCloumnStartIndex
          if (row.subSum) {
            const keys = row.keys
            const subSumColumnIndex = 1 + keys.length
            if (this.subSumDisiplayAll) {
              for (let j = 1; j < subSumColumnIndex; ++j) {
                arr[j] = { value: row[this.displayHeaders[j - 1].name] }
              }
            } else {
              if (keys.length === 1) {
                arr[1] = { value: keys[0] }
              } else {
                arr[1] = { rowspan: 1, colspan: keys.length, fixed: true }
              }
              for (let j = 2; j < subSumColumnIndex; ++j) {
                arr[j] = { rowspan: 0, colspan: 0, fixed: true }
              }
            }
            arr[subSumColumnIndex] = { rowspan: 1, colspan: row.subSumColspan, fixed: true }
            for (let j = subSumColumnIndex + 1; j < subSumColumnIndex + row.subSumColspan; ++j) {
              arr[j] = { rowspan: 0, colspan: 0, fixed: true }
            }
            spaceCloumnStartIndex = subSumColumnIndex + row.subSumColspan
          } else {
            arr[1] = { rowspan: 1, colspan: row.sumColspan, fixed: true }
            for (let j = 2; j < 1 + row.sumColspan; ++j) {
              arr[j] = { rowspan: 0, colspan: 0, fixed: true }
            }
            spaceCloumnStartIndex = 1 + row.sumColspan
          }
          const spaceColumn = { rowspan: 1, colspan: 1, fixed: true }
          let j = spaceCloumnStartIndex + 1
          for (; j < arr.length; ++j) {
            if (row.hasOwnProperty(this.displayHeaders[j - 1].name)) {
              break
            } else {
              spaceColumn.colspan += 1
              arr[j] = { rowspan: 0, colspan: 0, fixed: true }
            }
          }
          for (; j < arr.length; ++j) {
            arr[j] = { rowspan: 1, colspan: 1, fixed: true }
          }
          arr[spaceCloumnStartIndex] = spaceColumn
        } else {
          for (let j = 1; j < arr.length; ++j) {
            arr[j] = { value: row[this.displayHeaders[j - 1].name] }
          }
        }
        result[i] = arr
      }
      return result
    },
    onSortChange() {
      this.sortedRowData = [...this.rowData]
      const column = this.$refs.table.columns.find(p => p.order)
      if (column) {
        this.sortHeader = this.displayHeaders.find(p => p.name === column.property)
        this.sortType = column.order
        this.toOrderBy(this.sortedRowData, column.property, column.order, this.sortHeader)
      } else {
        this.sortHeader = null
      }
      for (let i = this.sortedRowData.length - 1; i >= 0; --i) {
        const row = this.sortedRowData[i]
        if (row.subSum || row.sum) {
          this.sortedRowData.splice(i, 1)
        }
      }
      if (this.rowData.length > 2) {
        const arr = []
        const map = new Map()
        for (const row of this.sortedRowData) {
          let subArr = map.get(row.groupNo)
          if (!subArr) {
            subArr = []
            arr.push(subArr)
            map.set(row.groupNo, subArr)
          }
          subArr.push(row)
        }
        const newSortedRowData = []
        for (const subArr of arr) {
          newSortedRowData.push(...subArr)
        }
        this.sortedRowData = newSortedRowData
      }
      let sumRow = null
      for (const row of this.statisticsRows) {
        if (row.subSum) {
          let j = this.sortedRowData.length - 1
          let isMatch = false
          for (; j >= 0; --j) {
            const sortedRow = this.sortedRowData[j]
            let k = 0
            for (; k < row.keys.length; ++k) {
              const sortRowValue = sortedRow.subSum && k < sortedRow.keys.length ? sortedRow.keys[k] : sortedRow[this.displayHeaders[k].name]
              if (typeof row.keys[k] !== 'number' && typeof sortRowValue === 'number') {
                row.keys[k] = Number(row.keys[k])
                row[this.displayHeaders[k].name] = row.keys[k]
              }
              if (row.keys[k] !== sortRowValue) {
                break
              }
            }
            if (k === row.keys.length) {
              isMatch = true
              break
            }
          }
          if (isMatch) {
            this.sortedRowData.splice(j + 1, 0, row)
          }
        } else if (row.sum) {
          sumRow = row
        }
      }
      if (sumRow) {
        this.sortedRowData.push(sumRow)
      }
      this.spanMatrixData = this.createSpanMatrixData()
      this.$refs.table.$refs.bodyWrapper.scrollTop = 0
      this.currentScrollTop = 0
      this.currentStartIndex = 0
      this.currentEndIndex = this.currentStartIndex + this.showRowNum
      this.onPageChange()
    },
    onSortMethod(a, b) {
      const column = this.$refs.table.columns.find(p => p.order)
      let sign = 1
      if (column && column.order === 'descending') { // 降序需要取反
        sign = -1
      }
      return (a._seq - b._seq) * sign
    },
    toOrderBy(data, property, order, sortHeader) {
      const sign = order === 'ascending' ? 1 : '-1'
      const isAsc = order === 'ascending'
      const isStringValue = sortHeader && sortHeader.dataType === 'String'
      const isDoubleValue = sortHeader && sortHeader.dataType === 'Double'
      data.sort(function(a, b) {
        let value1 = a[property]
        let value2 = b[property]
        if (isStringValue) {
          if (typeof value1 === 'string' && typeof value2 === 'string') {
            for (let i = 0; i < value1.length && i < value2.length; ++i) {
              const aCode = value1.charCodeAt(i)
              const bCode = value2.charCodeAt(i)
              let diff = 0
              if (aCode >= 256 && bCode >= 256) {
                if (isAsc) {
                  diff = value1[i].localeCompare(value2[i], 'zh')
                } else {
                  diff = value2[i].localeCompare(value1[i], 'zh')
                }
              } else {
                if (isAsc) {
                  diff = aCode - bCode
                } else {
                  diff = bCode - aCode
                }
              }
              if (diff !== 0) {
                return diff
              }
            }
            return isAsc ? value1.length - value2.length : value2.length - value1.length
          } else {
            if (value1 > value2) {
              return 1 * sign
            } else if (value1 < value2) {
              return -1 * sign
            } else {
              return 0
            }
          }
        } else {
          if (isDoubleValue) {
            if (value1 && value1.toString().lastIndexOf('%') >= 0) {
              value1 = Number(value1.toString().substr(0, value1.toString().length - 1))
            }
            if (value2 && value2.toString().lastIndexOf('%') >= 0) {
              value2 = Number(value2.toString().substr(0, value2.toString().length - 1))
            }
          }
          if (value1 > value2) {
            return 1 * sign
          } else if (value1 < value2) {
            return -1 * sign
          } else {
            return 0
          }
        }
      })
    },
    onPageChange() {
      this.currentData = this.sortedRowData.filter((item, index) => {
        if (index < this.currentStartIndex) {
          return false
        } else if (index > this.currentEndIndex) {
          return false
        } else {
          return true
        }
      })
      if (this.displayHeaders.length > 0) {
        for (const header of this.displayHeaders) {
          const column = this.$refs.table.columns.find(p => p.property === header.name)
          if (header._width && column && header._width !== column.width) {
            continue
          }
          header._width = this.getTextWidth(header.label) + 26
          for (const row of this.currentData) {
            const cellValue = row[header.name]
            const w = this.getTextWidth(this.toFormatter(header, cellValue)) + 2
            if (w > header._width) {
              header._width = w
            }
          }
        }
        const lastHeader = this.displayHeaders[this.displayHeaders.length - 1]
        lastHeader._width += 23
      }
      this.currentSpanData = this.spanMatrixData.filter((item, index) => {
        if (index < this.currentStartIndex) {
          return false
        } else if (index > this.currentEndIndex) {
          return false
        } else {
          return true
        }
      })
      for (let i = 0; i < this.currentData.length; ++i) {
        this.currentData[i]._seq = this.currentStartIndex + i + 1
        const rowObj = this.currentSpanData[i]
        if (!rowObj) {
          continue
        }
        for (let j = 0; j < rowObj.length; ++j) {
          const obj = rowObj[j]
          if (!obj) {
            continue
          }
          if (!obj.fixed) {
            delete obj.rowspan
            delete obj.colspan
          }
        }
      }
    },
    getTextWidth(text, fontSizePlus = 0) {
      return getLength(String(text)) * 0.5 * (16 + fontSizePlus) // 文本字节数 * 0.5 * font-size
    },
    onSpanMethod({ rowIndex, columnIndex }) {
      const rowObj = this.currentSpanData[rowIndex]
      if (!rowObj) {
        return { rowspan: 1, colspan: 1 }
      }
      const obj = rowObj[columnIndex]
      if (!obj) {
        return { rowspan: 1, colspan: 1 }
      }
      if (obj.hasOwnProperty('rowspan') && obj.hasOwnProperty('colspan')) {
        return { rowspan: obj.rowspan, colspan: obj.colspan }
      }
      if (columnIndex < this.spanStartIndex || !this.displayHeaders[columnIndex - 1].group) {
        return { rowspan: 1, colspan: 1 }
      }
      let rowspan = 1
      for (let i = rowIndex + 1; i < this.currentSpanData.length; ++i) {
        let isCrossRow = false
        const e = this.currentSpanData[i][columnIndex]
        if (e.value === obj.value) {
          if (columnIndex > this.spanStartIndex) {
            const preObj = this.currentSpanData[i][columnIndex - 1]
            if (preObj.rowspan === 0 && preObj.colspan === 0) {
              isCrossRow = true
            }
          } else {
            isCrossRow = true
          }
        }
        if (isCrossRow) {
          rowspan += 1
          e.rowspan = 0
          e.colspan = 0
        } else {
          break
        }
      }
      obj.rowspan = rowspan
      obj.colspan = 1
      return { rowspan: obj.rowspan, colspan: obj.colspan }
    },
    onRowClassName({ row, rowIndex }) {
      if (row.subSum || row.sum) { // 统计行
        return 'statistics-row'
      }
      return ''
    },
    onCellClassName({ row, column, rowIndex, columnIndex }) {
      rowIndex += this.currentStartIndex
      if (row.subSum && columnIndex === 1) { // 小计行标题列
        return 'subSum-title'
      }
      if (typeof this.cellClassName === 'function') {
        return this.cellClassName({ row, column, rowIndex, columnIndex })
      }
      return ''
    },
    onCellClick(row, column) {
      const headerIndex = this.displayHeaders.findIndex(p => p.name === column.property)
      const header = this.displayHeaders[headerIndex]
      this.$emit('cellClick', row, header, headerIndex)
    },
    onMouseover(rowSeq, headerIndex) {
      this.mouseoverRowSeq = rowSeq
      this.mouseoverHeaderIndex = headerIndex
    },
    onScroll(e) {
      if (this.timerId > 0) {
        clearTimeout(this.timerId)
      } else {
        this.startScrollLeft = e.target.scrollLeft
      }
      this.timerId = setTimeout(() => {
        this.timerId = 0
        if (e.target.scrollLeft === this.startScrollLeft) { // 忽略竖向滚动
          return
        }
        this.spanStartIndex = 1
        const headerWrapper = this.$refs.table.$refs.headerWrapper
        const tr = headerWrapper.querySelector('tr')
        for (let i = 1; i < tr.children.length; ++i) { // 忽略索引列
          if (this.isInView(tr.children[i])) {
            this.spanStartIndex = i
            break
          }
        }
        this.spanMatrixData = this.createSpanMatrixData()
      }, 100)
    },
    isInView(e) {
      const viewWidth = window.innerWidth || document.documentElement.clientWidth
      const viewHeight = window.innerHeight || document.documentElement.clientHeight
      const { top, left, right, bottom } = e.getBoundingClientRect()
      return (top > 0 && top < viewHeight && left > 0 && left < viewWidth) ||
        (top > 0 && top < viewHeight && right > 0 && right < viewWidth) ||
        (bottom > 0 && bottom < viewHeight && left > 0 && left < viewWidth) ||
        (bottom > 0 && bottom < viewHeight && right > 0 && right < viewWidth)
    },
    formatTextToNumber(text) {
      text = text.replaceAll(',', '')
      if (isNumber2(text)) {
        return Number(text)
      }
      return NaN
    }
  }
}
</script>

<style lang="scss" scoped>
.dw-report::v-deep.el-table {
  &.el-table--border {
    border: 1px solid #bfbfbf;
  }
  height: 100%;
  display: flex;
  flex-direction: column;
  .el-table__header-wrapper {
    flex-shrink: 0;
    table {
      padding-right: 22px;
      thead {
        tr {
          th {
            &.ascending {
              div.cell {
                .caret-wrapper {
                  .sort-caret.descending {
                    border-top-color: #CCCCCC;
                  }
                }
              }
            }
            &.descending {
              div.cell {
                .caret-wrapper {
                  .sort-caret.ascending {
                    border-bottom-color: #CCCCCC;
                  }
                }
              }
            }
            div.cell {
              color: #333333;
              .caret-wrapper {
                .sort-caret {
                  &.ascending {
                    border-bottom-color: #333333;
                  }
                  &.descending {
                    border-top-color: #333333;
                  }
                }
              }
            }
          }
        }
      }
    }
    th {
      background: #f3f3f3;
    }
  }
  .el-table__body-wrapper {
    &.is-scrolling-right {
      padding-right: 0;
    }
    flex-grow: 1;
    overflow-y: auto;
    padding-right: 22px;
    table {
      tbody {
        tr {
          td {
            div.cell {
              color: #333333;
            }
          }
        }
      }
    }
    .statistics-row {
      background: #f8fcd5;
    }
    .subSum-title {
      background: white;
    }
    tr:hover {
      .subSum-title {
        background: #f0f3f8;
      }
    }
  }
  thead {
    tr:first-child {
      th {
        &.header-focus {
          background: #F1F3F7;
        }
        background: #f8fcd5;
      }
    }
  }
  tr {
    td:first-child {
      background: #f8fcd5;
      div.cell-focus {
        background: #F1F3F7;
      }
    }
    td:last-child {
      div.cell {
        padding-right: 23px;
      }
    }
  }
  th.el-table__cell,
  td.el-table__cell {
    border-right: 1px solid #bfbfbf;
    border-bottom: 1px solid #bfbfbf;
    padding: 0;
    .cell {
      padding: 0;
      min-width: unset;
    }
  }
}
</style>
